import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, setBearerToken } from "@/api/client";

import { useEmbeddedSessionMutation } from "./api";

/** `sessionStorage` — sekme/iframe kapaninca kaybolur, bellek disi bearer tasiyicisi. */
const TOKEN_STORAGE_KEY_PREFIX = "asakai.embed.token";

/**
 * İNCELEME BULGUSU: anahtar `storeName`e göre KAPSANMALI — aksi halde ayni
 * sekmede (sessionStorage sekme kapanana kadar kalici) once magaza A icin
 * kurulan bearer, kullanici sonra magaza B'nin ikas embed'ini actiginda
 * YANLISLIKLA yeniden kullanilir (backend POST /embedded-session storeName'i
 * DOGRULAMAZ, yalniz mevcut principal.tenant_id'yi bearer'a gomer — bu yuzden
 * yanlis magazanin token'i baska bir magazanin verisini donduren gecerli bir
 * bearer olarak kabul edilir). `storeName` bos string olabilir (backend
 * yalniz varligini arar) — bu da kendi ayri "bosluk" anahtarina duser, zararsiz.
 */
function tokenStorageKey(storeName: string): string {
  return `${TOKEN_STORAGE_KEY_PREFIX}:${storeName}`;
}

export type EmbedSessionStatus = "loading" | "ready" | "unauthorized" | "error";

export interface EmbedSessionHandle {
  status: EmbedSessionStatus;
  /**
   * Onbellekteki (sessionStorage) ya da bellekteki bearer artik GECERSIZ
   * demektir (ör. `embedded-session` 2 saatlik omrunu doldurmus, sonraki
   * `/cards`/`/kpi/snapshot` cagrisi 401 donmus) — token temizlenir, durum
   * `"unauthorized"`a doner (bkz. `EmbedSummary`: veri sorgulari 401 aldiginda
   * bunu cagirir). Cagiran taraf ayrica YENIDEN embedded-session POST
   * ATMAZ — kullanici "tam ekranda giris yap" baglantisina yonlendirilir
   * (sozlesme: 2 saatlik omur dolunca oturum sessizce yenilenmez).
   */
  reportInvalid: () => void;
}

function readCachedToken(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // gizli sekme / depolama engelli — token yalniz bellekte tutulur
  }
}

function writeCachedToken(key: string, token: string): void {
  try {
    sessionStorage.setItem(key, token);
  } catch {
    // sessizce yut — kalici olmasa da bu oturumda bellekteki bearer yeterli
  }
}

function clearCachedToken(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // sessizce yut
  }
}

/**
 * ikas gomulu oturum kurulumu — docs/faz3-ikas-spec.md §6: `POST
 * /api/v2/ikas/embedded-session {storeName}` kullanici zaten cerezle
 * girisliyse 2 saatlik bearer jetonu doner; girisli degilse 401 →
 * `"unauthorized"` (sayfa "tam ekranda giris yap" gosterir). Onceki
 * cagridan kalan bir jeton `sessionStorage`'da varsa (ör. React StrictMode
 * cift-render'i ya da ayni sekmede yeniden mount) tekrar POST ATILMAZ —
 * ANCAK o onbellekteki jeton 2 saatlik omrunu doldurmus da olabilir; bu
 * durumu `reportInvalid()` KAPATIR (bkz. yukarida).
 *
 * `enabled=false` (ör. sayfa iframe DISINDA acildiginda, /ozet'e yonlendirme
 * yolda) hicbir istek ATMAZ — cagiran, kosulsuz Hook kurali geregi bu Hook'u
 * her render'da cagirir, gercek is bu bayrakla ertelenir.
 */
export function useEmbedSession(storeName: string, enabled = true): EmbedSessionHandle {
  const [status, setStatus] = useState<EmbedSessionStatus>("loading");
  const mutation = useEmbeddedSessionMutation();
  const startedRef = useRef(false);
  // reportInvalid TAM OLARAK bu render icin kurulan magazanin anahtarini
  // temizleyebilsin diye — storeName efekt bagimliligi DEGIL (asagida), bu
  // yuzden storeName'in KENDISI degil, efektin ILK (ve tek) calismasinda
  // COZULEN anahtar burada saklanir.
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const key = tokenStorageKey(storeName);
    keyRef.current = key;

    const cached = readCachedToken(key);
    if (cached) {
      setBearerToken(cached);
      setStatus("ready");
      return;
    }

    mutation.mutate(storeName, {
      onSuccess: (token) => {
        writeCachedToken(key, token);
        setBearerToken(token);
        setStatus("ready");
      },
      onError: (err) => {
        setStatus(err instanceof ApiError && err.status === 401 ? "unauthorized" : "error");
      },
    });
    // storeName kasitli izlenmiyor — oturum yalniz ilk yuklemede TEK SEFER kurulur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const reportInvalid = useCallback(() => {
    if (keyRef.current) clearCachedToken(keyRef.current);
    setBearerToken(null);
    setStatus("unauthorized");
  }, []);

  return { status, reportInvalid };
}
